/**
 * @fileoverview Shared schema for the detailed test-coverage report artifact
 * (`backend/data/coverage-report.json`). Generated by `npm run metrics` from
 * vitest's `coverage/coverage-final.json` and served via
 * GET /api/code-health/coverage. Drives the styled, drill-down coverage page —
 * the in-app equivalent of istanbul's `coverage/index.html`.
 */

import { z } from 'zod';

/** Covered/total counts plus the derived percentage for one coverage category. */
export const CoverageMetricSchema = z.object({
  covered: z.number(),
  total: z.number(),
  pct: z.number(),
});

/** The four istanbul coverage categories for a single file. */
export const CoverageSummarySchema = z.object({
  statements: CoverageMetricSchema,
  branches: CoverageMetricSchema,
  functions: CoverageMetricSchema,
  lines: CoverageMetricSchema,
});

/** Per-line coverage status, mirroring istanbul's source highlighting. */
export const CoverageLineStatusSchema = z.enum(['covered', 'uncovered', 'partial', 'neutral']);

/** One source line: its number, execution count (null when not a statement), status, and text. */
export const CoverageLineSchema = z.object({
  line: z.number(),
  hits: z.number().nullable(),
  status: CoverageLineStatusSchema,
  text: z.string(),
});

/** A single file's full coverage detail: summary metrics plus annotated source. */
export const CoverageFileSchema = z.object({
  /** Repo-relative POSIX path, e.g. `frontend/features/code-health/utils/codeHealthMetrics.ts`. */
  path: z.string(),
  summary: CoverageSummarySchema,
  lines: z.array(CoverageLineSchema),
});

/** The full report artifact: when it was built, repo totals, and every file. */
export const CoverageReportSchema = z.object({
  generatedAt: z.string(),
  total: CoverageSummarySchema,
  files: z.array(CoverageFileSchema),
});

/** Returned by GET /api/code-health/coverage when no report has been generated yet. */
export const CoverageReportEmptySchema = z.object({
  generatedAt: z.null(),
});

export type CoverageMetric = z.infer<typeof CoverageMetricSchema>;
export type CoverageSummary = z.infer<typeof CoverageSummarySchema>;
export type CoverageLineStatus = z.infer<typeof CoverageLineStatusSchema>;
export type CoverageLine = z.infer<typeof CoverageLineSchema>;
export type CoverageFile = z.infer<typeof CoverageFileSchema>;
export type CoverageReport = z.infer<typeof CoverageReportSchema>;
